// Runtime-neutral crypto + canonicalisation for the device-identity contract.
//
// THIS IS THE SINGLE SOURCE OF TRUTH for the canonical byte encodings a device
// signs and a server verifies. It is shared across apps (Locus, lens) so there
// is exactly one copy of these byte encodings — a drift here would break every
// existing device signature. Touches only TextEncoder / JSON / string math —
// identical in browser and Workers, so it is safe in SPA, Pages Functions, and
// any other consumer.
//
// Server-only helpers (importEd25519PublicKey / verifySignature) live in
// `./verify`. Client-only signing helpers stay app-side (e.g. Locus's
// `shared/sign.ts`). The byte encodings below MUST stay identical to the
// vectors every shipped device already signs against.

// Module-level TextEncoder singleton. TextEncoder is a standard global in both
// browsers and the Workers runtime, so one stateless instance is safe in either
// consumer and avoids a per-canonicalisation allocation.
const enc = new TextEncoder();

export interface PingLike {
  t: number;
  lat: number;
  lng: number;
  precision_meters: number | null;
  // Vertical pair (ADR 0013). MSL altitude + its accuracy radius; both
  // nullable, both ride together. Canon position: directly after the
  // horizontal-quality key (precision_meters), before the session_id tail.
  altitude_meters: number | null;
  altitude_accuracy_meters: number | null;
  session_id: number | null;
  // Per-ping authenticated data payload (ADR 0023). base64url, no padding; a
  // non-null string ONLY on an entitled device's data ping, else absent/null.
  // CONDITIONAL in the canon — present iff a non-null string (see
  // canonicalEnvelope). Optional so existing call sites and free-tier pings
  // need not mention it.
  data?: string | null;
}

export function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64urlDecode(s: string): Uint8Array {
  // base64url → base64 → bytes
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function canonicalEnvelope(
  token: string,
  pings: PingLike[],
  timestamp: number,
  nonce: string,
): Uint8Array {
  // Insertion order: token, pings, timestamp, nonce.
  // Per-ping order: t, lat, lng, precision_meters, altitude_meters,
  // altitude_accuracy_meters, session_id, [data].
  // The first seven keys are ALWAYS serialised; null when absent. No whitespace,
  // no replacer.
  //
  // `data` (ADR 0023) is the lone CONDITIONAL key: appended LAST, present iff
  // `p.data` is a non-null string — a no-data ping OMITS the key entirely (NOT
  // `data:null`), so its bytes are byte-identical to the pre-0023 canon. That is
  // the whole backward-compat trick (contrast ADR 0013's altitude, which was
  // unconditional and re-broke every signer). The predicate here is a PURE
  // presence check, never validation: this module stays dependency-free (it cannot
  // import wire types), and validity (the 64-byte decode) is owned by `isPing` at
  // ingest, which 400-rejects before this runs. The signer and the verifier
  // BOTH apply this identical `typeof p.data === 'string'` rule, so the bytes
  // match — this is the TS/Kotlin/.mjs parity surface.
  const canonical = {
    token,
    pings: pings.map((p) => {
      const c: {
        t: number;
        lat: number;
        lng: number;
        precision_meters: number | null;
        altitude_meters: number | null;
        altitude_accuracy_meters: number | null;
        session_id: number | null;
        data?: string;
      } = {
        t: p.t,
        lat: p.lat,
        lng: p.lng,
        precision_meters: p.precision_meters,
        altitude_meters: p.altitude_meters,
        altitude_accuracy_meters: p.altitude_accuracy_meters,
        session_id: p.session_id,
      };
      if (typeof p.data === 'string') c.data = p.data;
      return c;
    }),
    timestamp,
    nonce,
  };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalSessionStart(
  token: string,
  action: 'start',
  started_at: number,
  name: string | null,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, started_at, name, nonce, timestamp.
  const canonical = { token, action, started_at, name, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalSessionStop(
  token: string,
  action: 'stop',
  session_id: number,
  ended_at: number,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, session_id, ended_at, nonce, timestamp.
  const canonical = { token, action, session_id, ended_at, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalClaim(
  token: string,
  action: 'claim',
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, nonce, timestamp. No whitespace.
  // Signed by the device's existing ed25519 key to prove possession when an
  // authenticated user claims ownership (ADR 0014).
  const canonical = { token, action, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalDeviceRead(
  token: string,
  action: 'read',
  since: number | null,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, since, nonce, timestamp. No whitespace.
  // Mirrors canonicalClaim's token/action/nonce/timestamp shape, with `since`
  // (the read-window query param, a number or null) folded in. Signed by the
  // device's ed25519 key to authorise a full-precision GET of its OWN
  // non-public pings (the owner self-read; ADR 0003 §"Signed reads", ADR 0015).
  // `since` MUST equal the value the server parses from the `?since` query
  // param (null when the param is absent), or verification fails.
  const canonical = { token, action, since, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export interface ShareScope {
  range: '24h' | '7d' | 'all';
  precision: 'exact' | '1km' | 'city';
  expires: '1h' | '1d' | '1w' | 'never';
}

export interface CohortScope {
  range: '24h' | '7d' | 'all';
  precision: 'exact' | '1km' | 'city';
}

export function canonicalCohortScope(
  token: string,
  action: 'cohort-scope',
  cohort_scope: CohortScope,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, cohort_scope, nonce, timestamp.
  // cohort_scope key order: range, precision.
  // The signed write that sets a device's per-device cohort scope (Phase 2).
  // No `expires` (a membership is on/off, not a TTL — that field is share-only,
  // hence a distinct canon from canonicalShareMint). Signed by the device's
  // ed25519 key (key possession governs config — same custody as the ping/share
  // write paths, no separate sign-in). See the cohort-scope ADR.
  const canonical = {
    token,
    action,
    cohort_scope: {
      range: cohort_scope.range,
      precision: cohort_scope.precision,
    },
    nonce,
    timestamp,
  };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalDeviceTag(
  token: string,
  action: 'tag',
  tag: string | null,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, tag, nonce, timestamp. No whitespace.
  // The signed write that sets a device's human-friendly DISPLAY tag — a
  // mutable name layered over the immutable, token-derived label/animals
  // (ADR 0022). `tag` is the new value, or null to CLEAR it; the empty
  // string is normalised to null by BOTH signer and verifier, so a clear is
  // always canonicalised as `null` (the two must agree byte-for-byte). A
  // separate canon from canonicalEnvelope (the ping canon stays
  // byte-identical) and from canonicalCohortScope — possession of the
  // device's ed25519 key authorises the rename, the same key custody as the
  // ping/cohort-scope/share write paths (no sign-in; tagging is free-tier,
  // gated only by key possession).
  const canonical = { token, action, tag, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export interface DeviceCapabilities {
  query: boolean;
  command: boolean;
}

export function canonicalCapabilities(
  token: string,
  action: 'capabilities',
  capabilities: DeviceCapabilities,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, capabilities, nonce, timestamp.
  // capabilities key order: query, command.
  // The signed write that DECLARES a device's participation in the
  // server→client messaging channel (ADR 0024). `query` and `command` are
  // separately advertised — a device may decline either or both tiers — and
  // declaration is device-key-signed (the server can never fabricate a
  // capability; the owner can never enable a capability the device refuses).
  // No `expires` (capability is on/off, like the cohort scope).
  const canonical = {
    token,
    action,
    capabilities: {
      query: capabilities.query,
      command: capabilities.command,
    },
    nonce,
    timestamp,
  };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalCommandResult(
  token: string,
  action: 'command-result',
  command_id: number,
  result: string | null,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, command_id, result, nonce, timestamp.
  // The signed write a device uses to RETURN a result for an enqueued
  // command/query (ADR 0024). `result` is the JSON-shaped result payload as a
  // string (or null if the device chose to ack with no body); binding it into
  // the canon means the single signature authenticates the result bytes
  // exactly. `command_id` binds the result to the right enqueued row — a
  // signature for one id can't be replayed against another.
  const canonical = { token, action, command_id, result, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalGeofenceEvent(
  token: string,
  action: 'geofence-event',
  geofence_id: number,
  transition: 'enter' | 'exit',
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, action, geofence_id, transition, nonce, timestamp.
  // The signed write a device uses to REPORT a geofence breach (ADR 0026) — the
  // structural mirror of canonicalCommandResult. A spontaneous enter/exit has no
  // enqueued command_id to ack, so it gets its own canon + write rather than
  // riding command-results. `geofence_id` binds the breach to a specific fence (a
  // signature for one fence can't be replayed against another); `transition` is
  // exactly 'enter' | 'exit'. Signed by the device's ed25519 key — possession is
  // the only credential, so a breach is always that device's own report, never a
  // named human (the ADR 0025 single-issuer argument). This is a TS/Kotlin/.mjs
  // parity surface: add a JVM vector when the Android client lands.
  const canonical = { token, action, geofence_id, transition, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export function canonicalShareMint(
  token: string,
  share_scope: ShareScope,
  nonce: string,
  timestamp: number,
): Uint8Array {
  // Insertion order: token, share_scope, nonce, timestamp.
  // share_scope key order: range, precision, expires.
  const canonical = {
    token,
    share_scope: {
      range: share_scope.range,
      precision: share_scope.precision,
      expires: share_scope.expires,
    },
    nonce,
    timestamp,
  };
  return enc.encode(JSON.stringify(canonical));
}
